import { useEffect, useState } from "react";
import "./admin-email.css";
import { MdOutlineAdd } from "react-icons/md";
import { FaSearch } from "react-icons/fa";
import { HashLoader } from "react-spinners";
import ToastNotification, {
  showToast,
} from "../../components/toasts/ToastNotification";
import AddEmailModal from "../../components/modal/add-email-modal";
import { GrFormPrevious, GrFormNext } from "react-icons/gr";
import { MdDelete } from "react-icons/md";
import { deleteEmailForAdmin, getAllEmailsForAdmin } from "../../api/gmail.api";
import ConfirmModal from "../../components/modal/confirm-modal";
import { MdMarkEmailUnread } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { FaCopy } from "react-icons/fa6";
import { hasPermission } from "../../utils/auth";
import { useUser } from "../../context/UserContext";
import Pagination from "../../components/pagination/Pagination";
import { useDebounce } from "../../hooks/useDebounce";

const ITEMS_PER_PAGE = 8;

export const AdminEmail = () => {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearchInput = useDebounce(searchInput, 500);

  const [emails, setEmails] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIdToDelete, setSelectedIdToDelete] = useState("");
  const navigate = useNavigate();
  const { user } = useUser();

  useEffect(() => {
    document.title = "Admin - Email";
  }, []);

  useEffect(() => {
    fetchEmails();
  }, [currentPage, debouncedSearchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchInput]);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const response = await getAllEmailsForAdmin(
        ITEMS_PER_PAGE,
        currentPage,
        debouncedSearchInput.trim()
      );

      if (response.success) {
        let filtered = response.data.emails;

        if (searchInput.trim()) {
          filtered = filtered.filter((em) =>
            em.email.toLowerCase().includes(searchInput.toLowerCase())
          );
        }
        setEmails(filtered);
        setTotalPages(response.data.totalPages || 1);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleDeleteEmail = async () => {
    try {
      setLoading(true);
      setIsDeleteModalOpen(false);
      const res = await deleteEmailForAdmin(selectedIdToDelete);

      if (res.success) {
        showToast("Xóa email thành công", "success");
        fetchEmails();
        setSelectedIdToDelete("");
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text) => {
    await navigator.clipboard.writeText(text);
    showToast("Đã copy dữ liệu", "success");
  }

  if (loading) {
    return (
      <div className="loader-container">
        <HashLoader color="#092339" />
      </div>
    );
  }

  return (
    <div className="admin-email-container">
      <ToastNotification />
      <AddEmailModal
        title="Thêm Email"
        message="Email và mật khẩu sẽ được tạo tự động"
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        setEmails={setEmails}
      />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onConfirm={handleDeleteEmail}
        onClose={() => setIsDeleteModalOpen(false)}
        message={"Xác nhận bạn đang xóa một email"}
        title={"Xóa email"}
      />

      <p className="tab-nav-title">Danh sách email</p>
      <div className="admin-product-header">
        <div className="search-filter-container">
          <div className="admin-search-input-container">
            <input
              type="text"
              placeholder="Tìm kiếm theo email"
              className="admin-search-input"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setCurrentPage(1);
              }}
            />
            <FaSearch className="admin-search-icon" />
          </div>
        </div>
        {hasPermission(user?.role, "create:email") &&
          <a
            style={{ cursor: "pointer" }}
            onClick={() => setIsModalOpen(true)}
            className="add-product-button-container"
          >
            <MdOutlineAdd className="add-icon" />
            <p className="add-text">Thêm email mới</p>
          </a>}
      </div>

      <div className="product-table-container">
        <table className="product-table">
          <thead>
            <tr>
              <th>STT</th>
              <th>ID</th>
              <th>Email</th>
              <th>Mật khẩu</th>
              <th>Thời gian</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {emails.length > 0 ? (
              emails.map((email, index) => (
                <tr key={email._id}>
                  <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                  <td>{email?._id}</td>
                  <td>
                    <div className="email-copy-row">
                      <p>{email.email}</p>
                      <FaCopy onClick={() => handleCopy(email.email)} className="email-copy-icon" />
                    </div>
                  </td>
                  <td>
                    <div className="email-copy-row">
                      <p>{email.password}</p>
                      <FaCopy onClick={() => handleCopy(email.password)} className="email-copy-icon" />
                    </div>
                  </td>
                  <td>{new Date(email.createdAt).toLocaleString()}</td>
                  <td className="action-cell">
                    <div className="action-buttons-container">
                      <button
                        onClick={() =>
                          navigate(`/super-admin/emails/view/${email.email}`)
                        }
                        className="edit-btn action-button"
                      >
                        <MdMarkEmailUnread className="action-icon" />
                      </button>
                      {hasPermission(user?.role, "delete:email") &&
                        <button
                          onClick={() => {
                            setSelectedIdToDelete(email._id);
                            setIsDeleteModalOpen(true);
                          }}
                          className="delete-btn action-button"
                        >
                          <MdDelete className="action-icon" />
                        </button>}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6">Không có email</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={handlePrevPage}
          onNext={handleNextPage}
        />
      )}
    </div>
  );
};
